// src/pages/dashboard/components/RecentWorkoutsCard.jsx
import { useMemo, useState } from "react";
import { format } from "date-fns";
import legextension from "../../../../assets/images/legextension.png";
import treadmillIcon from "../../../../assets/images/treadmill.png";
import cycle from "../../../../assets/images/cycle.png";
import stepper from "../../../../assets/images/stepper.png";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { useLatestWorkoutDay } from "../../../../hooks/useWorkouts";
import { useNavigate } from "react-router-dom";

/* ───────── 포맷터 ───────── */
const fmt = {
    km: (v) => (v == null ? null : `${Number(v).toFixed(2)}km`),
    min: (v) => (v == null ? null : `${v}분`),
    kph: (v) => (v == null ? null : `${v}km/h`),
    kcal: (v) => (v == null ? null : `${v}kcal`),
    kg: (v) => (v == null ? null : `${v}kg`),
    watt: (v) => (v == null ? null : `${v}W`),
};

/* ───────── 머신 타이틀 한글 매핑 ───────── */
const MACHINE_KEY_TO_KO = {
    "leg extension": "레그익스텐션",
    "seated leg press": "시티드 레그프레스",
    "seated leg curl": "시티드 레그컬",
    "seated chest press": "시티드 체스트프레스",
    "butterfly": "버터플라이",
    "incline press": "인클라인 프레스",
    "lat pulldown": "랫풀다운",
    "long pull": "롱풀",
    "rear delt fly": "리어 델트플라이",
    "shoulder press": "숄더프레스",
    "arm curl": "암컬",
    "dipping": "디핑",
    "abdominal": "업도미널",
    "rotary torso": "로타리 토르소",
    "inner outer thigh": "이너/아웃싸이",
};

// 근력/머신 타이틀 한글 변환용 (WorkoutDayDetail와 동일 컨셉)
function getMachineDisplayTitle(w = {}) {
    const rawTitle = (w.title ?? "").trim();
    if (rawTitle && MACHINE_KEY_TO_KO[rawTitle]) {
        return MACHINE_KEY_TO_KO[rawTitle];
    }

    // 혹시 category에 영어로 들어오는 경우도 대비
    const cat = (w.category ?? "").trim();
    if (cat && MACHINE_KEY_TO_KO[cat]) {
        return MACHINE_KEY_TO_KO[cat];
    }

    // 이미 한글이 들어온 경우 그대로 사용
    if (rawTitle) return rawTitle;

    return "운동";
}



/* ───────── 유산소/머신 판별 ───────── */
function isCardio(w = {}) {
    const t = String(w.title ?? "").toLowerCase();
    if (String(w.kind ?? "").toLowerCase() === "cardio") return true;
    if (
        ["treadmill", "cycle", "stepper"].includes(
            String(w.category ?? "").toLowerCase()
        )
    )
        return true;
    return (
        t.includes("러닝머신") ||
        t.includes("러닝") ||
        t.includes("treadmill") ||
        t.includes("자전거") ||
        t.includes("사이클") ||
        t.includes("cycle") ||
        t.includes("스텝머신") ||
        t.includes("스텝") ||
        t.includes("stepper") ||
        t.includes("계단")
    );
}

// 유산소 3종 이름 정규화
function cardioName(w = {}) {
    const t = String(w.title ?? "").toLowerCase();
    if (t.includes("러닝머신") || t.includes("러닝") || t.includes("treadmill"))
        return "러닝머신";
    if (t.includes("자전거") || t.includes("사이클") || t.includes("cycle"))
        return "자전거";
    if (t.includes("스텝머신") || t.includes("스텝") || t.includes("stepper"))
        return "스텝머신";
    const c = String(w.category ?? "").toLowerCase();
    if (c === "treadmill") return "러닝머신";
    if (c === "cycle") return "자전거";
    if (c === "stepper") return "스텝머신";
    return "러닝머신";
}

/* ───────── 아이콘/필드 메타 (WorkoutDayDetail과 동일) ───────── */
const EXERCISE_META = {
    "러닝머신": {
        icon: treadmillIcon,
        fields: [
            ["운동속도", (w) => fmt.kph(w?.cardio?.speed_kph ?? w?.speed_kph)],
            ["시간", (w) => fmt.min(w?.duration_min)],
            ["거리", (w) => fmt.km(w?.cardio?.distance_km ?? w?.distance_km)],
            // ["심박수", (w) => fmt.km(w?.cardio?.distance_km ?? w?.distance_km)],
        ],
    },
    "자전거": {
        icon: cycle,
        fields: [
            ["watt", (w) => fmt.watt(w?.cardio?.watt_w ?? w?.watt_w)],
            ["시간", (w) => fmt.min(w?.duration_min)],
        ],
    },
    "스텝머신": {
        icon: stepper,
        fields: [
            ["watt", (w) => fmt.watt(w?.cardio?.watt_w ?? w?.watt_w)],
            ["시간", (w) => fmt.min(w?.duration_min)],
        ],
    },
    "머신": {
        icon: legextension,
        fields: [
            ["소모 칼로리", (w) => fmt.kcal(w?.calories_kcal)],
        ],
    },
};

/* ───────── 표시에 쓰는 이름 (WorkoutDayDetail과 동일) ───────── */
function normalizeTitle(title = "") {
    const t = String(title).toLowerCase();
    if (t.includes("러닝머신") || t.includes("러닝") || t.includes("treadmill"))
        return "러닝머신";
    if (t.includes("자전거") || t.includes("사이클") || t.includes("cycle"))
        return "자전거";
    if (t.includes("스텝머신") || t.includes("스텝") || t.includes("stepper"))
        return "스텝머신";
    return "머신";
}

function pickMeta(w) {
    if (isCardio(w)) return EXERCISE_META[cardioName(w)];
    return EXERCISE_META["머신"];
}

/* ───────── 카드에 뿌릴 라인 생성 (WorkoutDayDetail과 동일) ───────── */
function metricLines(w) {
    const meta = pickMeta(w);
    return meta.fields
        .map(([label, getter]) => {
            const val = getter(w);
            return val ? [label, val] : null;
        })
        .filter(Boolean);
}

/* ───────── 개별 최근 운동 아이템 (WorkoutItem과 동일 디자인) ───────── */
function RecentWorkoutItem({ w }) {
    const meta = pickMeta(w);
    const isCardioType = isCardio(w);

    // 세트 펼치기 상태 관리 (근력운동용)
    const [isExpanded, setIsExpanded] = useState(false);

    const sets = Array.isArray(w.sets) ? w.sets : [];
    const totalSets = sets.length;
    const shouldCollapse = !isCardioType && totalSets > 4;

    const visibleSets = shouldCollapse && !isExpanded ? sets.slice(0, 4) : sets;
    const hiddenCount = totalSets - visibleSets.length;

    return (
        <article
            className={`
                relative rounded-xl border border-gray-200 p-4 transition shadow-sm
                hover:bg-gray-50
            `}
        >
            <div className="grid grid-cols-[30%_65%] gap-4 items-start">
                {/* 좌측 아이콘 영역 (동일) */}
                <div className="rounded-full flex items-center justify-center self-center">
                    <img
                        src={meta.icon}
                        alt={`${normalizeTitle(w.title)} 아이콘`}
                        className="w-20 h-20 md:w-24 md:h-24 object-contain"
                    />
                </div>

                {/* 오른쪽 정보 영역 (동일) */}
                <div className="space-y-2 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-x-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <h4 className="font-bold text-lg md:text-xl truncate text-gray-800">
                                {isCardioType ? cardioName(w) : getMachineDisplayTitle(w)}
                            </h4>
                            {/* 근력운동: 총 세트 배지 */}
                            {!isCardioType && totalSets > 0 && (
                                <span className="shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                    {totalSets}세트
                                </span>
                            )}
                        </div>
                    </div>

                    <hr className="border-gray-100" />

                    {/* 상세 정보 영역 */}
                    {isCardioType ? (
                        // 유산소 운동: 요약 정보 (동일)
                        (() => {
                            const metrics = metricLines(w);
                            return (
                                <dl className="grid grid-cols-2 gap-y-1 pt-1 text-sm">
                                    {metrics.length > 0 ? (
                                        metrics.flatMap(([k, v], i) => [
                                            <dt
                                                key={`k-${i}`}
                                                className="text-gray-500 font-medium"
                                            >
                                                {k}
                                            </dt>,
                                            <dd
                                                key={`v-${i}`}
                                                className="text-left text-gray-700"
                                            >
                                                {v}
                                            </dd>,
                                        ])
                                    ) : (
                                        <div className="col-span-2 text-gray-400 text-sm">
                                            상세 정보 없음
                                        </div>
                                    )}
                                </dl>
                            );
                        })()
                    ) : (
                        // 근력/머신 운동: 세트 테이블 (동일)
                        <div className="pt-1">
                            {totalSets > 0 ? (
                                <>
                                    <table className="w-full table-fixed text-sm border-separate border-spacing-y-1">
                                        <colgroup>
                                            <col className="w-[15%]" />
                                            <col className="w-[55%]" />
                                            <col className="w-[30%]" />
                                        </colgroup>
                                        <thead className="text-xs text-gray-400 font-normal">
                                            <tr>
                                                <th className="text-center pb-1">
                                                    SET
                                                </th>
                                                <th className="text-center pb-1">
                                                    무게
                                                </th>
                                                <th className="text-center pb-1">
                                                    횟수
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-700">
                                            {visibleSets.map((set, index) => (
                                                <tr
                                                    key={index}
                                                    className="rounded-sm"
                                                >
                                                    <td className="text-center py-1 rounded-l-md text-xs text-gray-500">
                                                        {index + 1}
                                                    </td>
                                                    <td className="text-center py-1 font-medium">
                                                        {fmt.kg(
                                                            set.weight_kg
                                                        ) || "-"}
                                                    </td>
                                                    <td className="text-center py-1 rounded-r-md">
                                                        {set.reps != null
                                                            ? `${set.reps}회`
                                                            : "-"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* 세트 펼치기/접기 버튼 (동일) */}
                                    {shouldCollapse && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsExpanded((v) => !v);
                                            }}
                                            className="w-full mt-2 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        >
                                            {isExpanded ? (
                                                <>
                                                    <span>접기</span>
                                                    <ChevronUpIcon className="w-3 h-3" />
                                                </>
                                            ) : (
                                                <>
                                                    <span>
                                                        {hiddenCount}개 세트 더
                                                        보기
                                                    </span>
                                                    <ChevronDownIcon className="w-3 h-3" />
                                                </>
                                            )}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="text-gray-400 text-sm py-2">
                                    기록된 세트가 없습니다
                                </div>
                            )}

                            {/* 추가 정보 (칼로리 등) */}
                            <div className="text-right text-xs text-gray-400 mt-2">
                                {metricLines(w)
                                    .map(
                                        ([k, v]) =>
                                            `${k}: ${v}`
                                    )
                                    .join(" | ")}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}

/* ───────── 대시보드 최근 운동 카드 ───────── */
export default function RecentWorkoutsCard() {
    const navigate = useNavigate();
    const { data, isLoading } = useLatestWorkoutDay();
    const dateObj = data?.date ?? null;
    const workouts = data?.workouts ?? [];



    // 화면용 라벨 (원래 있던 거)
    const dateLabel = useMemo(() => {
        if (!dateObj) return null;
        const d = new Date(dateObj);
        if (isNaN(d.getTime())) return "날짜 알 수 없음"; // 🔹 유효하지 않은 날짜 방어막
        return format(d, "yyyy년 M월 d일");
    }, [dateObj]);

    // 🔹 링크에 쓸 YYYY-MM-DD 형식 날짜
    const dateForLink = useMemo(() => {
        if (!dateObj) return null;
        const d = new Date(dateObj);
        if (isNaN(d.getTime())) return null; // 🔹 유효하지 않은 날짜 방어막
        return format(d, "yyyy-MM-dd");
    }, [dateObj]);

    // 최근 운동일 기준, 최대 3개까지만 보여주기
    const top = useMemo(
        () => (Array.isArray(workouts) ? workouts.slice(0, 1) : []),
        [workouts]
    );

    const handleClickCard = () => {
        if (!dateForLink) return;          // 날짜 없으면 아무 것도 안 함
        navigate(`/schedule/day?date=${dateForLink}`);
    };

    return (
        <div
            onClick={handleClickCard}
            className={dateForLink ? "cursor-pointer" : ""}
        >
            {isLoading ? (
                <div className="py-4 text-center text-sm text-gray-400">
                    최근 운동을 불러오는 중이에요…
                </div>
            ) : workouts.length === 0 ? (
                <div
                    className="h-32 flex flex-col items-center justify-center 
                               bg-gray-50 rounded-2xl border-2 border-gray-200 border-dashed 
                               text-gray-400 cursor-pointer transition hover:bg-gray-100"
                >

                    <p className="mt-2 text-xs font-medium">
                        최근 운동 기록이 없습니다.
                    </p>
                </div>
            ) : (

                <>
                    <div className="space-y-3">
                        {top.map((w) => (
                            <RecentWorkoutItem key={w.id} w={w} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
